import { TranslatePipe } from "@ngx-translate/core";

import { ViewportScroller } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { Router } from "@angular/router";

import { GuidPipe } from "../../../helpers/guidpipe/guidpipe";
import { isUntaggedPort } from "../../../helpers/untaggedport";
import { phraseDetailPath } from "../../../pages/subscription-detail/subscription-detail-redirection";
import { AuthService } from "../../../services/authservice";
import { MessageService } from "../../../services/messageservice";
import { serviceHealth } from "../../dashboard/service-health-signal/service-health-signal";
import { PortVisualiser } from "../port-visualiser/port-visualiser";
import { ServiceTitleBlock } from "../service-title-block/service-title-block";

export interface PortSummaryService {
  subscriptionId: string;
  customerDescription: string;
  description: string;
  tags: string[];
  amountReserved: number;
  portSpeed: number;
  customer?: { fullname: string; customerId: string } | null;
  product: { tag: string; type: string };
  services: Array<{
    subscriptionId: string;
    description: string;
    customerDescription: string;
    percentage: number;
    product: { type: string; tag: string };
  }>;
  locations: Array<{
    address?: {
      city?: string | null;
      street?: string | null;
      number?: string | null;
      addition?: string | null;
    } | null;
  }>;
}

@Component({
  selector: "port-summary",
  templateUrl: "port-summary.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [GuidPipe, PortVisualiser, ServiceTitleBlock, TranslatePipe],
})
export class PortSummary {
  private auth = inject(AuthService);
  private msgsrv = inject(MessageService);
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);

  protected service = input.required<PortSummaryService>();

  protected subscriptionInstanceId = input<string>();
  protected forceTrafficBottom = input(false);
  protected vlanranges = input<string[]>([]);

  protected isAccessible = computed(
    () => this.auth.viewingCustomer().customerId === this.service().customer?.customerId,
  );
  protected health = serviceHealth(this.service);

  protected subscriptionId = computed(() => this.service().subscriptionId);
  private product = computed(() => this.service().product);
  protected tags = computed(() => this.service().tags);
  private isMsc = computed(() => this.product().tag.startsWith("MSC"));
  protected showVlanranges = computed(() => this.vlanranges()?.length > 0 && !this.isMsc());
  protected vlanrangeRows = computed(() =>
    this.vlanranges().map((vlanrange) => ({ vlanrange, untagged: isUntaggedPort(vlanrange) })),
  );

  protected organisationName = computed(() => this.service().customer?.fullname ?? "-");

  protected title = computed(() => this.service().customerDescription ?? this.service().description);

  protected locations = computed(() =>
    this.service().locations.map(
      (l) => `${l.address?.city} ${l.address?.street} ${l.address?.number}${l.address?.addition ?? ""}`,
    ),
  );

  protected navigateToService(service: PortSummaryService["services"][number]) {
    this.router.navigate(phraseDetailPath(service.product.type, service.product.tag, service.subscriptionId));
  }

  protected navigateToChart() {
    this.viewportScroller.scrollToAnchor("chart");
    const filterProps = {
      connection: this.service(),
      vlanranges: this.vlanranges(),
    };

    this.msgsrv.setChartFilter(filterProps);
  }
}
